import { useCallback } from 'react';

import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useEditCanvasStore } from '../store';
import { type AppNode } from '../types';
import { buildImageNodes } from '../utils/canvas-asset-drop/buildImageNodes';
import { buildVideoNodes } from '../utils/canvas-asset-drop/buildVideoNodes';
import {
  CANVAS_IMAGE_NODE_SPACING,
  DEFAULT_CANVAS_IMAGE_HEIGHT,
  DEFAULT_CANVAS_IMAGE_WIDTH,
} from '../utils/canvas-asset-drop/canvasAssetDropConstants';
import {
  type CanvasAssetPayload,
  type ImageNodeSource,
  type ImageNodesLayout,
} from '../utils/canvas-asset-drop/canvasAssetDropTypes';
import { fetchAsFile } from '../utils/fetchAsFile';
import { commands, useCommandHistory } from './commands';
import { useUploadAndPreload } from './useUploadAndPreload';
import { useZoomToFit } from './useZoomToFit';

export type AddImageNodeParams = {
  imageUrl: string;
  originalUrl: string;
  fileName: string;
  width: number;
  height: number;
  position?: { x: number; y: number };
  versionId?: string;
  assetId?: string;
  kind?: 'image' | 'video';
  duration?: number;
  size?: number;
  ext?: string;
  /** Asserts `imageUrl`/`originalUrl` are already canvas CDN URLs, so the upload pipeline can be skipped. */
  mediaIsCanvasNative?: boolean;
};

export const useAddImageNode = () => {
  const { execute } = useCommandHistory();
  const { uploadAndPreload } = useUploadAndPreload();
  const { showToast } = useToasts();
  const { zoomToFit } = useZoomToFit();

  const addImageNode = useCallback(
    async ({
      imageUrl,
      originalUrl,
      fileName,
      width,
      height,
      position = { x: 0, y: 0 },
      versionId,
      assetId,
      mediaIsCanvasNative,
    }: AddImageNodeParams): Promise<string | undefined> => {
      const imageNodeId = crypto.randomUUID();

      // Direct inserts skip the upload pipeline: workspace assets (assetId + versionId) already have
      // canvas-usable media, and canvas-native URLs would only re-upload identical bytes.
      if ((assetId && versionId) || mediaIsCanvasNative) {
        const imageNode: AppNode = {
          data: {
            ...(assetId && versionId ? { assetId, versionId } : undefined),
            fileName,
            media: { image: imageUrl, original: originalUrl },
            source: assetId && versionId ? 'workspace' : 'manual-edit',
            type: 'image',
          },
          id: imageNodeId,
          position,
          style: { height, width },
          type: 'image',
        };

        execute(commands.addNodes([imageNode]));

        return imageNode.id;
      }

      const previewId = crypto.randomUUID();

      // Show an upload preview node immediately for visual feedback.
      // Added via direct setState (not command history) because preview nodes are ephemeral.
      const previewNode: AppNode = {
        data: { blobUrl: imageUrl, fileName, mediaKind: 'image' },
        id: previewId,
        position,
        style: { height, width },
        type: 'canvasMediaUploadPreview',
      };

      useEditCanvasStore.getState().addNodes([previewNode]);

      zoomToFit({ nodeIds: [previewId] });

      try {
        const uniqueUrls = [...new Set([imageUrl, originalUrl])];
        const files = await Promise.all(uniqueUrls.map((url) => fetchAsFile(url, fileName)));
        const results = await uploadAndPreload(files);

        // Map each source URL to its CDN URL so we can resolve image/original independently.
        const cdnBySourceUrl = new Map<string, string>();
        for (const [i, url] of uniqueUrls.entries()) {
          const result = results[i];

          if (result?.status !== 'fulfilled') {
            throw new Error('Upload failed');
          }

          cdnBySourceUrl.set(url, result.cdnUrl);
        }

        const imageCdnUrl = cdnBySourceUrl.get(imageUrl);
        const originalCdnUrl = cdnBySourceUrl.get(originalUrl);

        if (!imageCdnUrl || !originalCdnUrl) {
          throw new Error('Upload failed');
        }

        const media = { image: imageCdnUrl, original: originalCdnUrl };

        const imageNode: AppNode = {
          data: {
            fileName,
            media,
            source: 'manual-edit',
            type: 'image',
          },
          id: imageNodeId,
          position,
          style: { height, width },
          type: 'image',
        };

        execute(commands.addNodes([imageNode], { removeNodeIds: [previewId] }));

        return imageNodeId;
      } catch {
        // Remove the preview node on failure.
        useEditCanvasStore.setState((state) => ({
          nodes: state.nodes.filter((n) => n.id !== previewId),
        }));
        showToast('Failed to upload image', { color: 'red' });

        return undefined;
      }
    },
    [execute, showToast, uploadAndPreload, zoomToFit],
  );

  const addImageNodesInRow = useCallback(
    async (images: AddImageNodeParams[]): Promise<string[]> => {
      const eligible = images.filter((item): item is AddImageNodeParams & { assetId: string; versionId: string } =>
        Boolean(item.assetId && item.versionId),
      );

      if (eligible.length < images.length) {
        reportErrorToBugsnag({
          context: 'addImageNodesInRow skipped items missing assetId/versionId',
          error: new Error('addImageNodesInRow skipped items missing assetId/versionId'),
          metadata: { kept: eligible.length, requested: images.length },
        });
        showToast('Some assets could not be added to the Canvas', { color: 'red' });
      }

      if (eligible.length === 0) {
        return [];
      }

      const existing = useEditCanvasStore.getState().nodes;
      let startX = 0;
      let startY = 0;

      if (existing.length > 0) {
        let maxRight = Number.NEGATIVE_INFINITY;

        for (const n of existing) {
          const width = typeof n.style?.width === 'number' ? n.style.width : DEFAULT_CANVAS_IMAGE_WIDTH;
          const right = n.position.x + width;

          if (right > maxRight) {
            maxRight = right;
            startY = n.position.y;
          }
        }

        startX = maxRight + CANVAS_IMAGE_NODE_SPACING;
      }

      const layout: ImageNodesLayout = { kind: 'row', spacing: CANVAS_IMAGE_NODE_SPACING, startX, startY };
      const imageSources: ImageNodeSource[] = [];
      const videoPayloads: CanvasAssetPayload[] = [];

      for (const item of eligible) {
        if (item.kind === 'video') {
          videoPayloads.push({
            assetId: item.assetId,
            duration: item.duration,
            height: item.height || DEFAULT_CANVAS_IMAGE_HEIGHT,
            id: item.versionId,
            media: { image: item.imageUrl, original: item.originalUrl },
            title: item.fileName,
            width: item.width || DEFAULT_CANVAS_IMAGE_WIDTH,
          });
        } else {
          imageSources.push({
            assetId: item.assetId,
            fileName: item.fileName,
            height: item.height || DEFAULT_CANVAS_IMAGE_HEIGHT,
            media: { image: item.imageUrl, original: item.originalUrl },
            versionId: item.versionId,
            width: item.width || DEFAULT_CANVAS_IMAGE_WIDTH,
          });
        }
      }

      const { nodes: imageNodes, nextOffsetX } = buildImageNodes(imageSources, layout);
      const { nodes: videoNodes } = buildVideoNodes(videoPayloads, layout, nextOffsetX);
      const nodes = [...imageNodes, ...videoNodes];

      execute(commands.addNodes(nodes, { zoomToFitIfNeeded: true }));

      return nodes.map((node) => node.id);
    },
    [execute, showToast],
  );

  return { addImageNode, addImageNodesInRow };
};
